import { FormEvent, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Eye, EyeOff, LockKeyhole, Loader2, ShieldCheck, Sprout, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { BrandMark } from "@/components/BrandMark";
import { useAuth } from "@/services/supabase";

const trustItems = [
  { icon: ShieldCheck, title: "Secure by design", detail: "Role, project and geographic access controls" },
  { icon: WifiOff, title: "Field-ready", detail: "Designed for offline agricultural operations" },
  { icon: CheckCircle2, title: "Auditable records", detail: "Consent, evidence and activity traceability" },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, loading, error } = useAuth() ?? { signIn: async () => { throw new Error("Auth not available"); }, loading: false, error: null };
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const from = (location.state as { from?: string } | null)?.from || "/dashboard";

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setLocalError(null);
    if (!email || !password) {
      setLocalError("Please enter your work email and password.");
      return;
    }
    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch {
      // error state is set in the auth provider
    }
  };

  const displayError = localError || error;

  return (
    <main className="grid min-h-screen bg-background lg:grid-cols-[minmax(0,1fr)_minmax(480px,0.82fr)]" data-testid="login-page">
      <section className="login-trust-panel hidden min-h-screen p-10 text-white lg:flex lg:flex-col lg:justify-between xl:p-14" data-testid="login-trust-panel">
        <div className="relative z-10"><BrandMark variant="login" /></div>
        <div className="relative z-10 max-w-2xl py-16">
          <Badge className="mb-6 border-white/15 bg-white/10 text-white hover:bg-white/10" data-testid="login-project-badge"><Sprout className="mr-1 size-3" />ARISE — An ACZON Farms Ltd Project</Badge>
          <h1 className="max-w-xl text-balance text-4xl font-semibold leading-[1.12] tracking-tight xl:text-5xl" data-testid="login-hero-title">Better field intelligence, from farmer registration to every mapped plot.</h1>
          <p className="mt-6 max-w-xl text-base leading-7 text-white/68" data-testid="login-hero-description">A secure operations workspace for ACZON Farms Ltd teams coordinating farmer services across Kumi, Serere and Ngora.</p>
          <div className="mt-10 grid gap-4 sm:grid-cols-3" data-testid="login-trust-features">
            {trustItems.map(({ icon: Icon, title, detail }) => (
              <div key={title} className="rounded-xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur-sm" data-testid={`trust-item-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon className="size-5 text-white/80" aria-hidden="true" />
                <p className="mt-3 text-sm font-semibold">{title}</p><p className="mt-1 text-xs leading-5 text-white/58">{detail}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 flex items-center gap-3" data-testid="login-footer">
          <img src="/assets/logo/aczon-whiten.svg" alt="ACZON Farms Ltd" className="h-6 w-auto opacity-80" />
          <p className="text-xs text-white/48" data-testid="login-footer-copy">Crafting with nature</p>
        </div>
      </section>

      <section className="flex min-h-screen items-center justify-center px-5 py-10 sm:px-10" data-testid="login-form-panel">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden"><BrandMark variant="login" /></div>
          <div className="mb-6 flex items-center justify-between gap-3">
            <div><p className="text-sm font-semibold text-primary" data-testid="login-eyebrow">ARISE Project</p><h2 className="mt-1 text-3xl font-semibold tracking-tight" data-testid="login-title">Sign in to your workspace</h2></div>
          </div>
          <p className="mb-7 text-sm leading-6 text-muted-foreground" data-testid="login-description">Enter your work credentials to access the ARISE operations workspace.</p>

          <Card className="border-border/80 shadow-soft" data-testid="login-card">
            <CardHeader className="pb-4"><CardTitle className="text-base">Staff sign-in</CardTitle><CardDescription>Authorized ACZON personnel only</CardDescription></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="email">Work email</Label>
                  <Input id="email" type="email" value={email} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)} autoComplete="email" placeholder="you@aczonfarms.com" data-testid="login-email-input" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between"><Label htmlFor="password">Password</Label><Link to="/forgot-password" className="text-sm font-medium text-primary hover:underline" data-testid="login-forgot-password-link">Forgot password?</Link></div>
                  <div className="relative">
                    <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPassword(event.target.value)} autoComplete="current-password" className="pr-11" data-testid="login-password-input" />
                    <Button type="button" size="icon" variant="ghost" onClick={() => setShowPassword((value) => !value)} className="absolute right-1 top-1 size-8" aria-label={showPassword ? "Hide password" : "Show password"} data-testid="password-visibility-button">{showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}</Button>
                  </div>
                </div>
                {displayError && (
                  <p className="text-sm font-medium text-destructive" data-testid="login-error-message" role="alert">{displayError}</p>
                )}
                <Button type="submit" className="w-full" disabled={loading || !email || !password} data-testid="login-submit-button">
                  {loading ? <Loader2 className="mr-2 size-4 animate-spin" /> : <LockKeyhole className="mr-2 size-4" />}
                  {loading ? "Signing in..." : "Sign in"}
                </Button>
              </form>
            </CardContent>
          </Card>
          <p className="mt-6 text-center text-xs leading-5 text-muted-foreground" data-testid="login-security-note">Staff accounts are provisioned administratively. Contact your administrator for access.</p>
        </div>
      </section>
    </main>
  );
}
